/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

export default function AdminUserDetail({ user, admin, onBack, lang }) {
  const [profile, setProfile] = useState(null)
  const [directors, setDirectors] = useState([])
  const [shareholders, setShareholders] = useState([])
  const [compliance, setCompliance] = useState(null)
  const [banks, setBanks] = useState([])
  const [wallets, setWallets] = useState([])
  const [business, setBusiness] = useState(null)
  const [logs, setLogs] = useState([])
  const [adminDocs, setAdminDocs] = useState([])
  const [agreementsData, setAgreements] = useState(null)
  const [amlQuestionnaire, setAmlQuestionnaire] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [activeTab, setActiveTab] = useState('info')
  const [uploadForm, setUploadForm] = useState({ doc_type: '', description: '', file: null })

  const [reviewForm, setReviewForm] = useState({
    kyc_status: user.kyc_status || 'pending',
    kyc_level: user.kyc_level || 1,
    risk_level: user.risk_level || 'low',
    kyc_expires_at: user.kyc_expires_at ? user.kyc_expires_at.split('T')[0] : '',
    account_status: user.status || 'pending',
    kyc_vendor_reference: user.kyc_vendor_reference || '',
    kyc_vendor_notes: user.kyc_vendor_notes || '',
    notes: '',
  })

  const t = (en, zh) => lang === 'zh' ? zh : en
  const isKYC = user.customer_type === 'individual'
  const perms = admin.permissions || {}

  useEffect(() => { fetchAll() }, []) // eslint-disable-line react-hooks/exhaustive-deps

const fetchAll = async () => {
  setLoading(true)
  const queries = [
    isKYC
      ? supabase.from('user_kyc_profile').select('*').eq('user_id', user.id).single()
      : supabase.from('user_kyb_profile').select('*').eq('user_id', user.id).single(),
    isKYC
      ? supabase.from('kyc_compliance').select('*').eq('user_id', user.id).single()
      : supabase.from('kyb_compliance').select('*').eq('user_id', user.id).single(),
    supabase.from('user_bank_accounts').select('*').eq('user_id', user.id),
    supabase.from('user_wallets').select('*').eq('user_id', user.id),
    supabase.from('user_business_profile').select('*').eq('user_id', user.id).single(),
    supabase.from('kyc_review_logs').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
    supabase.from('admin_uploaded_documents').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
    supabase.from('user_agreements').select('*').eq('user_id', user.id).single(),
  ]

  if (!isKYC) {
    queries.push(supabase.from('kyb_directors').select('*').eq('user_id', user.id))
    queries.push(supabase.from('kyb_shareholders').select('*').eq('user_id', user.id))
    queries.push(supabase.from('kyb_aml_questionnaire').select('*').eq('user_id', user.id).single())
  }

  const results = await Promise.all(queries)
  setProfile(results[0].data)
  setCompliance(results[1].data)
  setBanks(results[2].data || [])
  setWallets(results[3].data || [])
  setBusiness(results[4].data)
  setLogs(results[5].data || [])
  setAdminDocs(results[6].data || [])
  setAgreements(results[7].data)

  if (!isKYC) {
    setDirectors(results[8].data || [])
    setShareholders(results[9].data || [])
    setAmlQuestionnaire(results[10].data)
  }
  setLoading(false)
}

  const handleSave = async () => {
    if (!perms.can_approve && !perms.can_reject && !perms.can_set_risk) {
      alert(t('You do not have permission to make changes', '您没有权限进行修改'))
      return
    }
    setSaving(true)
    try {
      const now = new Date().toISOString()
      const updateData = {}
      const logData = {
        user_id: user.id,
        admin_id: admin.id,
        admin_name: admin.name,
        notes: reviewForm.notes,
      }

      // KYC 状态变更
      if (perms.can_approve || perms.can_reject) {
        updateData.kyc_status = reviewForm.kyc_status
        updateData.kyc_vendor_reference = reviewForm.kyc_vendor_reference || null
        updateData.kyc_vendor_notes = reviewForm.kyc_vendor_notes || null
        logData.previous_status = user.kyc_status
        logData.new_status = reviewForm.kyc_status
        logData.action = `KYC Status: ${user.kyc_status} → ${reviewForm.kyc_status}`
        logData.kyc_vendor_reference = reviewForm.kyc_vendor_reference || null
        if (reviewForm.kyc_status === 'approved') updateData.kyc_verified_at = now
      }

      // KYC 等级变更
      if (perms.can_approve) {
        updateData.kyc_level = parseInt(reviewForm.kyc_level)
        logData.previous_kyc_level = user.kyc_level
        logData.new_kyc_level = parseInt(reviewForm.kyc_level)
      }

      // 风险等级变更
      if (perms.can_set_risk) {
        updateData.risk_level = reviewForm.risk_level
        logData.previous_risk_level = user.risk_level
        logData.new_risk_level = reviewForm.risk_level
      }

      // 账户状态变更
      if (perms.can_suspend) {
        updateData.status = reviewForm.account_status
        logData.previous_account_status = user.status
        logData.new_account_status = reviewForm.account_status
      }

      // KYC 到期时间
      if (reviewForm.kyc_expires_at) {
        updateData.kyc_expires_at = reviewForm.kyc_expires_at
        logData.expires_at = reviewForm.kyc_expires_at
      }

      await supabase.from('users').update(updateData).eq('id', user.id)
      await supabase.from('kyc_review_logs').insert(logData)

      alert(t('Saved successfully!', '保存成功！'))
      setReviewForm(prev => ({ ...prev, notes: '' }))
      fetchAll()
    } catch (err) {
      alert(`Error: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  const handleUpload = async () => {
    if (!perms.can_upload) {
      alert(t('You do not have permission to upload files', '您没有上传权限'))
      return
    }
    if (!uploadForm.file || !uploadForm.doc_type) {
      alert(t('Please select a file and document type', '请选择文件和文档类型'))
      return
    }
    setUploading(true)
    try {
      const ext = uploadForm.file.name.split('.').pop()
      const path = `admin-uploads/${user.id}/${Date.now()}.${ext}`
      const { error: uploadErr } = await supabase.storage.from('kyc-documents').upload(path, uploadForm.file)
      if (uploadErr) throw uploadErr
      const { data } = supabase.storage.from('kyc-documents').getPublicUrl(path)
      await supabase.from('admin_uploaded_documents').insert({
        user_id: user.id,
        admin_id: admin.id,
        admin_name: admin.name,
        file_url: data.publicUrl,
        file_name: uploadForm.file.name,
        doc_type: uploadForm.doc_type,
        description: uploadForm.description,
      })
      await supabase.from('kyc_review_logs').insert({
        user_id: user.id,
        admin_id: admin.id,
        admin_name: admin.name,
        action: `Uploaded document: ${uploadForm.doc_type} — ${uploadForm.file.name}`,
        notes: uploadForm.description,
      })
      setUploadForm({ doc_type: '', description: '', file: null })
      fetchAll()
      alert(t('File uploaded successfully!', '文件上传成功！'))
    } catch (err) {
      alert(`Upload error: ${err.message}`)
    } finally {
      setUploading(false)
    }
  }

  // ─── 样式组件 ─────────────────────────────────────────
  const InfoRow = ({ label, value }) => (
    <div style={{ display: 'flex', borderBottom: '1px solid #f3f4f6', padding: '10px 0' }}>
      <span style={{ width: '160px', flexShrink: 0, fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>{label}</span>
      <span style={{ fontSize: '13px', color: '#111827', wordBreak: 'break-all' }}>{value || '—'}</span>
    </div>
  )

  const FileLink = ({ label, url }) => {
    if (!url) return <InfoRow label={label} value={t('Not uploaded', '未上传')} />
    return (
      <div style={{ display: 'flex', borderBottom: '1px solid #f3f4f6', padding: '10px 0', alignItems: 'center' }}>
        <span style={{ width: '160px', flexShrink: 0, fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>{label}</span>
        {perms.can_view_files
          ? <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: '13px', color: '#3b82f6', textDecoration: 'none' }}>📄 {t('View File', '查看文件')} ↗</a>
          : <span style={{ fontSize: '13px', color: '#9ca3af' }}>🔒 {t('No permission', '无权限查看')}</span>}
      </div>
    )
  }

  const YesNoBadge = ({ value }) => (
    <span style={{ background: value ? '#fef2f2' : '#f0fdf4', color: value ? '#991b1b' : '#166534', padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' }}>
      {value ? 'YES ⚠' : 'NO ✓'}
    </span>
  )

  const PersonCard = ({ person, title }) => (
    <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '14px', marginBottom: '12px', border: '1px solid #e5e7eb' }}>
      <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>{title}</p>
      <InfoRow label={t('Full Name', '姓名')} value={person.full_name} />
      <InfoRow label={t('Nationality', '国籍')} value={person.nationality} />
      <InfoRow label={t('Email', '邮箱')} value={person.email} />
      <InfoRow label={t('Phone', '电话')} value={person.phone} />
      <InfoRow label={t('Address', '地址')} value={person.address} />
      <InfoRow label={t('ID Type', '证件类型')} value={person.id_type} />
      <InfoRow label={t('ID Number', '证件号码')} value={person.id_number} />
      <InfoRow label={t('ID Expiry', '到期日')} value={person.id_expiry_date} />
      <FileLink label={t('ID Front', '证件正面')} url={person.id_doc_url} />
      <FileLink label={t('ID Back', '证件背面')} url={person.id_back_url} />
      <FileLink label={t('Proof of Address', '地址证明')} url={person.proof_of_address_url} />
    </div>
  )

  const tabs = [
    { key: 'info', label: t('Profile', '基本资料') },
    !isKYC && { key: 'directors', label: t('Directors & Shareholders', '董事/股东') },
    { key: 'compliance', label: t('Compliance', '合规问卷') },
    !isKYC && profile?.is_financial_institution && { key: 'aml', label: '⚠ AML Questionnaire' },
    { key: 'financial', label: t('Bank & Wallet', '银行&钱包') },
    { key: 'documents', label: t('Documents', '文件管理') },
    { key: 'agreements', label: t('Agreements', '协议签署') },
    { key: 'logs', label: t('Audit Log', '审核记录') },
  ].filter(Boolean)

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Loading...</div>

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '20px', alignItems: 'start' }}>

      {/* 左侧内容 */}
      <div>
        {/* 用户头部卡片 */}
        <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: isKYC ? '#eff6ff' : '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
              {isKYC ? '👤' : '🏢'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                  {profile?.full_name || profile?.company_name || user.email}
                </h2>
                <span style={{ background: isKYC ? '#eff6ff' : '#f5f3ff', color: isKYC ? '#1d4ed8' : '#6d28d9', padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' }}>
                  {isKYC ? 'KYC Individual' : 'KYB Corporate'}
                </span>
                <span style={{ background: user.kyc_status === 'approved' ? '#f0fdf4' : user.kyc_status === 'rejected' ? '#fef2f2' : '#fef9c3', color: user.kyc_status === 'approved' ? '#166534' : user.kyc_status === 'rejected' ? '#991b1b' : '#854d0e', padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' }}>
                  {user.kyc_status}
                </span>
              </div>
              <p style={{ color: '#6b7280', fontSize: '13px', margin: '4px 0 0' }}>
                {user.user_number_text} · {user.email} · {user.phone} · {t('Registered', '注册于')} {new Date(user.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Tab 导航 */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', background: '#fff', borderRadius: '10px', padding: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', flexWrap: 'wrap' }}>
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ flex: 1, minWidth: '80px', padding: '8px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '500', background: activeTab === tab.key ? '#3b82f6' : 'transparent', color: activeTab === tab.key ? '#fff' : '#6b7280', transition: 'all .15s' }}>
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>

          {/* Tab: 基本资料 */}
          {activeTab === 'info' && (
            <div>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                {t('Personal / Company Information', '个人/公司信息')}
              </p>
              {isKYC && profile ? (
                <>
                  <InfoRow label={t('Full Name', '姓名')} value={profile.full_name} />
                  <InfoRow label={t('Date of Birth', '出生日期')} value={profile.date_of_birth} />
                  <InfoRow label={t('Nationality', '国籍')} value={profile.nationality} />
                  <InfoRow label={t('Country of Residence', '居住国家')} value={profile.country_residence} />
                  <InfoRow label={t('Address', '地址')} value={profile.address} />
                  <InfoRow label={t('Occupation', '职业')} value={profile.occupation} />
                  <InfoRow label={t('Employer', '雇主')} value={profile.employer} />
                  <InfoRow label={t('ID Type', '证件类型')} value={profile.id_type} />
                  <InfoRow label={t('ID Number', '证件号码')} value={profile.id_number} />
                  <InfoRow label={t('ID Expiry', '证件到期日')} value={profile.id_expiry_date} />
                  <div style={{ marginTop: '16px' }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>{t('Documents', '证件文件')}</p>
                    <FileLink label={t('ID Front', '证件正面')} url={profile.id_front_url} />
                    <FileLink label={t('ID Back', '证件背面')} url={profile.id_back_url} />
                    <FileLink label={t('Selfie with ID', '手持证件自拍')} url={profile.selfie_url} />
                    <FileLink label={t('Proof of Address', '地址证明')} url={profile.proof_of_address_url} />
                  </div>
                </>
              ) : profile ? (
                <>
                  <InfoRow label={t('Company Name', '公司名称')} value={profile.company_name} />
                  <InfoRow label={t('Company Type', '公司类型')} value={profile.company_type} />
                  <InfoRow label={t('Reg. Number', '注册号码')} value={profile.registration_number} />
                  <InfoRow label={t('Reg. Country', '注册国家')} value={profile.registration_country} />
                  <InfoRow label={t('Reg. Date', '注册日期')} value={profile.registration_date} />
                  <InfoRow label={t('Business Address', '公司地址')} value={profile.business_address} />
                  <InfoRow label={t('Website', '网站')} value={profile.website} />
                  <InfoRow label={t('Business Nature', '主营业务')} value={profile.primary_business_nature} />
                  <InfoRow label={t('Income Source', '收入来源')} value={profile.primary_income_source} />
                  <InfoRow label={t('Monthly Volume', '月交易量')} value={profile.monthly_volume_usd ? `$${Number(profile.monthly_volume_usd).toLocaleString()}` : null} />
                  <InfoRow label={t('Avg Ticket Size', '平均交易规模')} value={profile.average_ticket_size} />
                  {profile.has_license && (
                <>
                    <InfoRow label="Has Regulatory License" value="Yes ✓" />
                    <InfoRow label="License Details" value={profile.license_details} />
                    <InfoRow label="License Country" value={profile.license_country} />
                    <InfoRow label="Financial Institution License" value={profile.is_financial_institution ? '✅ Yes — AML Questionnaire Required' : 'No'} />
                </>
                )}
                {!profile.has_license && (
                <InfoRow label="Has Regulatory License" value="No" />
                )}
                  <div style={{ marginTop: '16px' }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>{t('Company Documents', '公司文件')}</p>
                    <FileLink label={t('Certificate of Incorporation', '注册证书')} url={profile.cert_incorporation_url} />
                    <FileLink label={t('Proof of Address', '地址证明')} url={profile.proof_address_url} />
                    <FileLink label={t('Ownership Structure', '股权结构')} url={profile.ownership_structure_url} />
                    <FileLink label={t('Bank Statement (3 months)', '银行流水（3个月）')} url={profile.bank_statement_url} />
                    <FileLink label={t('License Certificate', '牌照证书')} url={profile.license_cert_url} />
                  </div>
                </>
              ) : <p style={{ color: 'rgb(156, 163, 175)' }}>No profile data</p>}
            
                {/* 主联系人信息 */}
                <div style={{ marginTop: '20px' }}>
                <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                    Main Contact Person
                </p>
                <InfoRow label="Main Contact Email" value={user.main_contact_email} />
                <InfoRow label="Main Contact Phone" value={user.main_contact_phone} />
                </div>

              {business && (
                <div style={{ marginTop: '20px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                    {t('Business Information', '业务信息')}
                  </p>
                  <InfoRow label={t('Trade Purpose', '交易目的')} value={business.business_type} />
                  <InfoRow label={t('Experience', '交易经验')} value={business.trading_experience} />
                  <InfoRow label={t('Preferred Crypto', '常用加密货币')} value={business.preferred_crypto} />
                  <InfoRow label={t('Preferred Fiat', '常用法币')} value={business.preferred_fiat} />
                  <InfoRow label={t('Avg Trade Size', '平均交易规模')} value={business.average_trade_size} />
                  <InfoRow label={t('Trading Frequency', '交易频率')} value={business.trading_frequency} />
                </div>
              )}
            </div>
          )}

          {/* Tab: 董事/股东 (KYB only) */}
          {activeTab === 'directors' && !isKYC && (
            <div>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                {t('Directors', '董事信息')}
              </p>
              {directors.length === 0
                ? <p style={{ color: '#9ca3af', fontSize: '13px' }}>{t('No directors found', '没有董事信息')}</p>
                : directors.map((d, i) => <PersonCard key={i} person={d} title={`${t('Director', '董事')} ${i + 1}`} />)}

              <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '24px 0 16px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                {t('Shareholders / UBO', '股东 / 最终受益人')}
              </p>
              {shareholders.length === 0
                ? <p style={{ color: '#9ca3af', fontSize: '13px' }}>{t('No shareholders found', '没有股东信息')}</p>
                : shareholders.map((s, i) => (
                  <div key={i}>
                    <PersonCard person={s} title={`${t('Shareholder', '股东')} ${i + 1}${s.is_ubo ? ' 👑 UBO' : ''}`} />
                    <div style={{ marginTop: '-8px', marginBottom: '12px', paddingLeft: '14px' }}>
                      <InfoRow label={t('Ownership %', '持股比例')} value={s.ownership_percentage ? `${s.ownership_percentage}%` : null} />
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* Tab: 合规问卷 */}
          {activeTab === 'compliance' && (
            <div>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                {t('Compliance Questionnaire', '合规问卷')}
              </p>
              {compliance ? (
                (isKYC ? [
                  { label: 'Q1. PEP Status', key: 'is_pep', detailKey: 'is_pep_details' },
                  { label: 'Q2. Sanctions Related', key: 'is_sanctions_related', detailKey: 'is_sanctions_details' },
                  { label: 'Q3. FinconnectX Business Dealings', key: 'has_finconnectx_dealings', detailKey: 'finconnectx_details' },
                ] : [
                  { label: 'Q1. Sanctions List Involvement', key: 'is_on_sanctions_list', detailKey: 'is_on_sanctions_list_details' },
                  { label: 'Q2. Sanctions Country Related', key: 'is_sanctions_country_related', detailKey: 'is_sanctions_country_details' },
                  { label: 'Q3. Controlled by Sanctioned Person', key: 'is_controlled_by_sanctioned', detailKey: 'is_controlled_by_sanctioned_details' },
                  { label: 'Q4. FinconnectX Business Dealings', key: 'has_finconnectx_dealings', detailKey: 'finconnectx_details' },
                  { label: 'Q5. Improper Payments', key: 'has_improper_payments', detailKey: 'has_improper_payments_details' },
                ]).map(({ label, key, detailKey }) => (
                  <div key={key} style={{ marginBottom: '12px', padding: '12px', background: compliance[key] ? '#fef2f2' : '#f9fafb', borderRadius: '8px', border: `1px solid ${compliance[key] ? '#fecaca' : '#e5e7eb'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: compliance[key] && compliance[detailKey] ? '8px' : '0' }}>
                      <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{label}</span>
                      <YesNoBadge value={compliance[key]} />
                    </div>
                    {compliance[key] && compliance[detailKey] && (
                      <p style={{ fontSize: '13px', color: '#6b7280', margin: 0, paddingTop: '8px', borderTop: '1px solid #fecaca' }}>{compliance[detailKey]}</p>
                    )}
                  </div>
                ))
              ) : <p style={{ color: '#9ca3af', fontSize: '13px' }}>No compliance data</p>}
            </div>
          )}

          {/* Tab: 银行 & 钱包 */}
          {activeTab === 'financial' && (
            <div>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                {t('Bank Accounts', '银行账户')}
              </p>
              {banks.length === 0
                ? <p style={{ color: '#9ca3af', fontSize: '13px' }}>No bank accounts</p>
                : banks.map((b, i) => (
                  <div key={i} style={{ background: '#f9fafb', borderRadius: '8px', padding: '14px', marginBottom: '10px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600' }}>{b.bank_name}</span>
                      {b.is_primary && <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '1px 8px', borderRadius: '10px', fontSize: '11px' }}>Primary</span>}
                    </div>
                    <InfoRow label={t('Account Name', '账户名')} value={b.account_name} />
                    <InfoRow label={t('Account Number', '账号')} value={b.account_number} />
                    <InfoRow label={t('Country', '国家')} value={b.bank_country} />
                    <InfoRow label={t('Currency', '币种')} value={b.currency} />
                    {b.swift_code && <InfoRow label="SWIFT" value={b.swift_code} />}
                    {b.iban && <InfoRow label="IBAN" value={b.iban} />}
                    {b.notes && <InfoRow label={t('Other Fields', '其他字段')} value={b.notes} />}
                  </div>
                ))}

              <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '20px 0 12px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                {t('Crypto Wallets', '加密钱包')}
              </p>
              {wallets.length === 0
                ? <p style={{ color: '#9ca3af', fontSize: '13px' }}>No wallets</p>
                : wallets.map((w, i) => (
                  <div key={i} style={{ background: '#f9fafb', borderRadius: '8px', padding: '14px', marginBottom: '10px', border: '1px solid #e5e7eb' }}>
                    <InfoRow label={t('Network', '网络')} value={w.network} />
                    <InfoRow label={t('Address', '地址')} value={w.address} />
                    {w.label && <InfoRow label={t('Label', '备注')} value={w.label} />}
                  </div>
                ))}
            </div>
          )}

          {/* Tab: 文件管理 */}
          {activeTab === 'documents' && (
            <div>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                {t('Admin Uploaded Documents', '管理员上传文件')}
              </p>

              {/* 上传表单 */}
              {perms.can_upload && (
                <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '16px', marginBottom: '20px', border: '1px solid #e5e7eb' }}>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                    {t('Upload New Document', '上传新文件')}
                  </p>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>{t('Document Type *', '文件类型 *')}</label>
                    <select value={uploadForm.doc_type} onChange={e => setUploadForm(p => ({ ...p, doc_type: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: '#fff' }}>
                      <option value="">{t('Select type', '请选择')}</option>
                      {[
                        'ID Document', 'Proof of Address', 'Bank Statement',
                        'Source of Funds', 'Company Registration', 'License',
                        'Compliance Form', 'Other'
                      ].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>{t('Description', '描述')}</label>
                    <input value={uploadForm.description} onChange={e => setUploadForm(p => ({ ...p, description: e.target.value }))}
                      placeholder={t('Brief description of the file...', '文件简要描述...')}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>{t('File *', '文件 *')}</label>
                    <input type="file" accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                      onChange={e => setUploadForm(p => ({ ...p, file: e.target.files[0] }))}
                      style={{ fontSize: '13px' }} />
                  </div>
                  <button onClick={handleUpload} disabled={uploading}
                    style={{ padding: '8px 20px', background: uploading ? '#9ca3af' : '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: uploading ? 'not-allowed' : 'pointer', fontWeight: '500' }}>
                    {uploading ? t('Uploading...', '上传中...') : t('Upload File', '上传文件')}
                  </button>
                </div>
              )}

              {/* 已上传文件列表 */}
              {adminDocs.length === 0
                ? <p style={{ color: '#9ca3af', fontSize: '13px' }}>{t('No documents uploaded yet', '暂无上传文件')}</p>
                : adminDocs.map((doc, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f9fafb', borderRadius: '8px', marginBottom: '8px', border: '1px solid #e5e7eb' }}>
                    <span style={{ fontSize: '20px' }}>📄</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{doc.doc_type}</span>
                        <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '1px 8px', borderRadius: '10px', fontSize: '11px' }}>{doc.admin_name}</span>
                      </div>
                      {doc.description && <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0' }}>{doc.description}</p>}
                      <p style={{ fontSize: '11px', color: '#9ca3af', margin: '2px 0 0' }}>{new Date(doc.created_at).toLocaleString()}</p>
                    </div>
                    {perms.can_view_files && (
                      <a href={doc.file_url} target="_blank" rel="noreferrer"
                        style={{ fontSize: '13px', color: '#3b82f6', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                        {t('View', '查看')} ↗
                      </a>
                    )}
                  </div>
                ))}
            </div>
          )}

            {/* Tab: 协议签署详情 */}
            {activeTab === 'agreements' && (
            <div>
                <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                {t('Agreements & Confirmations', '协议签署记录')}
                </p>
                {agreementsData ? (
                <>
                    <div style={{ marginBottom: '12px', padding: '12px', background: agreementsData.terms_accepted ? '#f0fdf4' : '#fef2f2', borderRadius: '8px', border: `1px solid ${agreementsData.terms_accepted ? '#86efac' : '#fecaca'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '13px', color: '#374151' }}>Terms & Conditions</span>
                        <span style={{ fontSize: '12px', color: agreementsData.terms_accepted ? '#166534' : '#991b1b', fontWeight: '500' }}>
                        {agreementsData.terms_accepted ? '✓ Accepted' : '✗ Not Accepted'}
                        </span>
                    </div>
                    {agreementsData.terms_accepted_at && (
                        <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0' }}>
                        {new Date(agreementsData.terms_accepted_at).toLocaleString()}
                        </p>
                    )}
                    </div>

                    <div style={{ marginBottom: '12px', padding: '12px', background: agreementsData.privacy_accepted ? '#f0fdf4' : '#fef2f2', borderRadius: '8px', border: `1px solid ${agreementsData.privacy_accepted ? '#86efac' : '#fecaca'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '13px', color: '#374151' }}>Privacy Policy</span>
                        <span style={{ fontSize: '12px', color: agreementsData.privacy_accepted ? '#166534' : '#991b1b', fontWeight: '500' }}>
                        {agreementsData.privacy_accepted ? '✓ Accepted' : '✗ Not Accepted'}
                        </span>
                    </div>
                    {agreementsData.privacy_accepted_at && (
                        <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0' }}>
                        {new Date(agreementsData.privacy_accepted_at).toLocaleString()}
                        </p>
                    )}
                    </div>

                    {/* Unsolicited Request Confirmation 详情 */}
                    <div style={{ padding: '16px', background: agreementsData.unsolicited_request_accepted ? '#f0fdf4' : '#fef2f2', borderRadius: '8px', border: `1px solid ${agreementsData.unsolicited_request_accepted ? '#86efac' : '#fecaca'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Client Unsolicited Request Confirmation</span>
                        <span style={{ fontSize: '12px', color: agreementsData.unsolicited_request_accepted ? '#166534' : '#991b1b', fontWeight: '500' }}>
                        {agreementsData.unsolicited_request_accepted ? '✓ Confirmed' : '✗ Not Confirmed'}
                        </span>
                    </div>
                    {agreementsData.unsolicited_request_at && (
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>
                        Signed at: {new Date(agreementsData.unsolicited_request_at).toLocaleString()}
                        </p>
                    )}
                    <InfoRow label="Client Full Name" value={agreementsData.unsolicited_client_name} />
                    <InfoRow label="Date of Contact" value={agreementsData.unsolicited_contact_date} />
                    <InfoRow label="Services Requested" value={agreementsData.unsolicited_services} />
                    </div>
                </>
                ) : (
                <p style={{ color: '#9ca3af', fontSize: '13px' }}>No agreement data found</p>
                )}
            </div>
            )}

            {/* Tab: AML 问卷 */}
            {activeTab === 'aml' && (
            <div>
                <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                AML Questionnaire — Financial Institution
                </p>

                {amlQuestionnaire ? (
                <>
                    {[
                    { label: 'Q1. Licensed to perform business', key: 'is_licensed', detail: 'license_regulator' },
                    { label: 'Q2. AML compliance program required by law', key: 'has_aml_program', detail: 'has_aml_program_details'},
                    { label: 'Q3. Has AML policies and procedures', key: 'has_aml_policies', detail: 'has_aml_policies_details'},
                    { label: 'Q4. Has KYC program', key: 'has_kyc_program', detail: 'kyc_controls' },
                    { label: 'Q5. Has Enhanced Due Diligence (EDD)', key: 'has_edd', detail: 'edd_procedure' },
                    { label: 'Q6. Screens watchlist names', key: 'screens_watchlist', detail: 'watchlist_sources' },
                    { label: 'Q7. Monitors suspicious transactions', key: 'has_suspicious_monitoring', detail: 'suspicious_threshold' },
                    { label: 'Q8. Has transaction limits', key: 'has_transaction_limits', detail: 'transaction_limits' },
                    { label: 'Q9. Requests Source of Funds (SOF)', key: 'requests_sof', detail: 'sof_threshold' },
                    { label: 'Q10. Has high-risk customer procedure', key: 'has_high_risk_procedure', detail: 'high_risk_procedure' },
                    { label: 'Q11. Prohibits shell company business', key: 'prohibits_shell_companies',detail:'prohibits_shell_details'},
                    { label: 'Q12. Has MLRO officer', key: 'has_mlro',detail:'has_mlro_details' },
                    { label: 'Q13. Provides AML training to employees', key: 'provides_aml_training', detail: 'training_frequency' },
                    { label: 'Q14. Has independent AML audit', key: 'has_independent_audit', detail: 'last_audit_date' },
                    ].map(({ label, key, detail }) => (
                    <div key={key} style={{ marginBottom: '10px', padding: '12px', background: amlQuestionnaire[key] ? '#f0fdf4' : '#f9fafb', borderRadius: '8px', border: `1px solid ${amlQuestionnaire[key] ? '#86efac' : '#e5e7eb'}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', color: '#374151', flex: 1, paddingRight: '12px' }}>{label}</span>
                        <span style={{ background: amlQuestionnaire[key] ? '#f0fdf4' : '#fef2f2', color: amlQuestionnaire[key] ? '#166534' : '#991b1b', padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' }}>
                            {amlQuestionnaire[key] ? 'YES ✓' : 'NO'}
                        </span>
                        </div>
                        {detail && amlQuestionnaire[key] && amlQuestionnaire[detail] && (
                        <p style={{ fontSize: '12px', color: '#6b7280', margin: '6px 0 0', paddingTop: '6px', borderTop: '1px solid #d1fae5' }}>
                            {amlQuestionnaire[detail]}
                        </p>
                        )}
                    </div>
                    ))}

                    {/* 签字认证信息 */}
                    <div style={{ marginTop: '20px', padding: '16px', background: '#fef9c3', borderRadius: '8px', border: '1px solid #fde047' }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#854d0e', marginBottom: '12px' }}>
                        Certification / Signatory
                    </p>
                    <InfoRow label="Signatory Name" value={amlQuestionnaire.signatory_name} />
                    <InfoRow label="Title / Position" value={amlQuestionnaire.signatory_title} />
                    <InfoRow label="Company Name" value={amlQuestionnaire.company_name_cert} />
                    </div>
                </>
                ) : (
                <div style={{ padding: '20px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                    <p style={{ color: '#9ca3af', fontSize: '13px' }}>
                    {t('No AML questionnaire data found.', 'No AML questionnaire data found.')}
                    {' '}This user may not be a licensed financial institution.
                    </p>
                </div>
                )}
            </div>
            )}      
          
          {/* Tab: 审核记录 */}
          {activeTab === 'logs' && (
            <div>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                {t('Audit Log', '审核操作记录')}
              </p>
              {logs.length === 0
                ? <p style={{ color: '#9ca3af', fontSize: '13px' }}>{t('No review history yet', '暂无审核记录')}</p>
                : logs.map((log, i) => (
                  <div key={i} style={{ borderLeft: '3px solid #3b82f6', paddingLeft: '14px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{log.admin_name}</span>
                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#3b82f6', margin: '0 0 4px', fontWeight: '500' }}>{log.action}</p>
                    {log.previous_kyc_level !== log.new_kyc_level && log.new_kyc_level && (
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0' }}>
                        KYC Level: {log.previous_kyc_level} → {log.new_kyc_level}
                      </p>
                    )}
                    {log.previous_risk_level !== log.new_risk_level && log.new_risk_level && (
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0' }}>
                        Risk Level: {log.previous_risk_level} → {log.new_risk_level}
                      </p>
                    )}
                    {log.previous_account_status !== log.new_account_status && log.new_account_status && (
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0' }}>
                        Account Status: {log.previous_account_status} → {log.new_account_status}
                      </p>
                    )}
                    {log.kyc_vendor_reference && (
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0' }}>
                        Vendor Ref: {log.kyc_vendor_reference}
                      </p>
                    )}
                    {log.expires_at && (
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0' }}>
                        Expiry set to: {new Date(log.expires_at).toLocaleDateString()}
                      </p>
                    )}
                    {log.notes && (
                      <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0', fontStyle: 'italic' }}>
                        "{log.notes}"
                      </p>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* 右侧：审核操作面板 */}
      <div style={{ position: 'sticky', top: '76px' }}>
        <div style={{ background: '#fff', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
            {t('Review Actions', '审核操作')}
          </p>

          {/* 权限提示 */}
          {!perms.can_approve && !perms.can_reject && (
            <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: '6px', padding: '8px 12px', marginBottom: '14px', fontSize: '12px', color: '#854d0e' }}>
              🔒 {t('Read-only access', '只读权限')}
            </div>
          )}

          {/* KYC 状态 */}
          {(perms.can_approve || perms.can_reject) && (
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>
                {t('KYC Status', 'KYC状态')}
              </label>
              <select value={reviewForm.kyc_status} onChange={e => setReviewForm(p => ({ ...p, kyc_status: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: '#fff' }}>
                <option value="pending">Pending</option>
                <option value="approved">✅ Approved</option>
                <option value="rejected">❌ Rejected</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          )}

          {/* KYC Vendor Reference（仅 approve/reject 时显示） */}
          {(perms.can_approve || perms.can_reject) && (reviewForm.kyc_status === 'approved' || reviewForm.kyc_status === 'rejected') && (
            <div style={{ marginBottom: '14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '12px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>
                {t('KYC Vendor Reference', 'KYC服务商参考号')}
              </label>
              <input value={reviewForm.kyc_vendor_reference}
                onChange={e => setReviewForm(p => ({ ...p, kyc_vendor_reference: e.target.value }))}
                placeholder={t('e.g. SUMSUB-12345678', '如：SUMSUB-12345678')}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #86efac', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', background: '#fff' }} />
              <textarea value={reviewForm.kyc_vendor_notes}
                onChange={e => setReviewForm(p => ({ ...p, kyc_vendor_notes: e.target.value }))}
                placeholder={t('Vendor notes (optional)...', 'KYC服务商备注（可选）...')}
                rows={2}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #86efac', borderRadius: '6px', fontSize: '13px', marginTop: '6px', resize: 'vertical', boxSizing: 'border-box', background: '#fff' }} />
            </div>
          )}

          {/* KYC 等级 */}
          {perms.can_approve && (
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>
                {t('KYC Level', 'KYC等级')}
              </label>
              <select value={reviewForm.kyc_level} onChange={e => setReviewForm(p => ({ ...p, kyc_level: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: '#fff' }}>
                <option value={1}>Level 1</option>
                <option value={2}>Level 2</option>
                <option value={3}>Level 3</option>
              </select>
            </div>
          )}

          {/* 风险等级 */}
          {perms.can_set_risk && (
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>
                {t('Risk Level', '风险等级')}
              </label>
              <select value={reviewForm.risk_level} onChange={e => setReviewForm(p => ({ ...p, risk_level: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: '#fff' }}>
                <option value="low">🟢 Low</option>
                <option value="medium">🟡 Medium</option>
                <option value="high">🔴 High</option>
              </select>
            </div>
          )}

          {/* 账户状态 */}
          {perms.can_suspend && (
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>
                {t('Account Status', '账户状态')}
              </label>
              <select value={reviewForm.account_status} onChange={e => setReviewForm(p => ({ ...p, account_status: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: '#fff' }}>
                <option value="pending">Pending</option>
                <option value="active">✅ Active</option>
                <option value="suspended">⏸ Suspended</option>
                <option value="closed">🚫 Closed</option>
              </select>
            </div>
          )}

          {/* KYC 到期时间 */}
          {perms.can_approve && (
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>
                {t('KYC Expiry Date', 'KYC到期日')}
              </label>
              <input type="date" value={reviewForm.kyc_expires_at}
                onChange={e => setReviewForm(p => ({ ...p, kyc_expires_at: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
            </div>
          )}

          {/* 审核备注 */}
          {perms.can_add_notes && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>
                {t('Review Notes', '审核备注')}
              </label>
              <textarea value={reviewForm.notes} onChange={e => setReviewForm(p => ({ ...p, notes: e.target.value }))}
                placeholder={t('Add internal notes...', '添加内部备注...')}
                rows={3}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
          )}

          {(perms.can_approve || perms.can_reject || perms.can_set_risk || perms.can_suspend) && (
            <button onClick={handleSave} disabled={saving}
              style={{ width: '100%', padding: '11px', borderRadius: '8px', border: 'none', background: saving ? '#9ca3af' : '#10b981', color: '#fff', fontSize: '14px', fontWeight: '500', cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? t('Saving...', '保存中...') : t('Save Review', '保存审核结果')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}